import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { FiArrowLeft, FiX, FiLoader } from 'react-icons/fi'
import { RootState } from '@/store'
import { successStoriesApi } from '@/services/api/successStories'
import { CreateSuccessStoryData } from '@/types/successStory'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import SelectDropdown, { SelectOption } from '@/components/ui/SelectDropdown'
import { useSnackbarContext } from '@/components/ui/SnackbarProvider'
import { extractErrorMessage } from '@/utils/errorHandler'

type SuccessStoryCategoryValue = CreateSuccessStoryData['category']

const SubmitSuccessStoryPage: React.FC = () => {
    const navigate = useNavigate()
    const { isAuthenticated, user } = useSelector((state: RootState) => state.auth)
    const { showSuccess, showError } = useSnackbarContext()

    // Form state
    const [formData, setFormData] = useState<CreateSuccessStoryData>({
        title: '',
        category: 'technology',
        name: '',
        role: '',
        location: '',
        story: '',
        testimonial: '',
        achievement: '',
        skills: []
    })

    const [skillInput, setSkillInput] = useState('')
    const [loading, setLoading] = useState(false)
    const [draftLoaded, setDraftLoaded] = useState(false)
    const [skillError, setSkillError] = useState<string>('')

    // Load draft from localStorage on mount, with user data as fallback
    useEffect(() => {
        if (!isAuthenticated || !user) {
            navigate('/login')
            return
        }

        // Pre-fill data from user profile
        const userFullName = user.profile?.firstName && user.profile?.lastName
            ? `${user.profile.firstName} ${user.profile.lastName}`
            : user.name || ''

        const userLocation = user.profile?.location?.city || ''
        const userRole = user.profile?.company || ''

        // Try to load draft first
        const draft = localStorage.getItem('successStoryDraft')
        if (draft) {
            try {
                const loadedDraft = JSON.parse(draft)
                // Merge with defaults and user data to ensure all fields exist
                setFormData({
                    title: loadedDraft.title || '',
                    category: loadedDraft.category || 'technology',
                    name: loadedDraft.name || userFullName,
                    role: loadedDraft.role || userRole,
                    location: loadedDraft.location || userLocation,
                    story: loadedDraft.story || '',
                    testimonial: loadedDraft.testimonial || '',
                    achievement: loadedDraft.achievement || '',
                    skills: loadedDraft.skills || []
                })
            } catch (err) {
                console.error('Failed to load draft:', err)
                // If draft parsing fails, use user data
                setFormData(prev => ({
                    ...prev,
                    name: userFullName || prev.name,
                    role: userRole || prev.role,
                    location: userLocation || prev.location
                }))
            }
        } else {
            // No draft, pre-fill from user profile
            setFormData(prev => ({
                ...prev,
                name: userFullName || prev.name,
                role: userRole || prev.role,
                location: userLocation || prev.location
            }))
        }

        setDraftLoaded(true)
    }, [isAuthenticated, navigate, user])

    // Auto-save draft to localStorage
    useEffect(() => {
        if (draftLoaded) {
            localStorage.setItem('successStoryDraft', JSON.stringify(formData))
        }
    }, [formData, draftLoaded])

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const { name, value } = e.target
        setFormData(prev => ({
            ...prev,
            [name]: value
        }))
    }

    const handleCategoryChange = (value: string) => {
        setFormData(prev => ({
            ...prev,
            category: value as SuccessStoryCategoryValue
        }))
    }

    const handleAddSkill = () => {
        if (!skillInput.trim()) {
            setSkillError('Please enter a skill')
            return
        }

        if (formData.skills.includes(skillInput.trim())) {
            setSkillError('This skill is already added')
            return
        }

        if (formData.skills.length >= 10) {
            setSkillError('Maximum 10 skills allowed')
            return
        }

        setFormData(prev => ({
            ...prev,
            skills: [...prev.skills, skillInput.trim()]
        }))
        setSkillInput('')
        setSkillError('')
    }

    const handleRemoveSkill = (index: number) => {
        setFormData(prev => ({
            ...prev,
            skills: prev.skills.filter((_, i) => i !== index)
        }))
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        // Validation
        if (!formData.name.trim()) {
            showError('Please enter your name')
            return
        }

        if (formData.name.length < 2) {
            showError('Name must be at least 2 characters')
            return
        }

        if (formData.name.length > 100) {
            showError('Name cannot exceed 100 characters')
            return
        }

        if (!formData.role.trim()) {
            showError('Please enter your role')
            return
        }

        if (formData.role.length < 3) {
            showError('Role must be at least 3 characters')
            return
        }

        if (formData.role.length > 100) {
            showError('Role cannot exceed 100 characters')
            return
        }

        if (!formData.location.trim()) {
            showError('Please enter your location')
            return
        }

        if (formData.location.length < 3) {
            showError('Location must be at least 3 characters')
            return
        }

        if (formData.location.length > 100) {
            showError('Location cannot exceed 100 characters')
            return
        }

        if (!formData.title.trim()) {
            showError('Please enter a title')
            return
        }

        if (formData.title.length < 10) {
            showError('Title must be at least 10 characters')
            return
        }

        if (!formData.story.trim() || formData.story.length < 50) {
            showError('Story must be at least 50 characters')
            return
        }

        if (formData.story.length > 1000) {
            showError('Story must not exceed 1000 characters')
            return
        }

        if (!formData.testimonial.trim() || formData.testimonial.length < 30) {
            showError('Testimonial must be at least 30 characters')
            return
        }

        if (formData.testimonial.length > 500) {
            showError('Testimonial must not exceed 500 characters')
            return
        }

        if (!formData.achievement.trim() || formData.achievement.length < 10) {
            showError('Achievement must be at least 10 characters')
            return
        }

        if (formData.achievement.length > 100) {
            showError('Achievement must not exceed 100 characters')
            return
        }

        if (formData.skills.length === 0) {
            showError('Please add at least one skill')
            return
        }

        try {
            setLoading(true)
            const response = await successStoriesApi.createSuccessStory(formData)

            if (response.data?.story) {
                // Clear draft
                localStorage.removeItem('successStoryDraft')

                const successMessage = response.message || 'Success story submitted successfully!'
                showSuccess(successMessage, { duration: 3000 })

                // Redirect to success stories page after showing the success message
                setTimeout(() => {
                    navigate('/success-stories')
                }, 2500)
            } else {
                showError('Failed to submit story: Invalid response')
            }
        } catch (err: any) {
            const message = extractErrorMessage(err, 'Failed to submit story')
            showError(message)
            console.error('Error submitting story:', err)
        } finally {
            setLoading(false)
        }
    }

    if (!draftLoaded) {
        return (
            <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
                <LoadingSpinner />
            </div>
        )
    }

    const storyLength = formData.story.length
    const testimonialLength = formData.testimonial.length
    const achievementLength = formData.achievement.length

    const categoryOptions: SelectOption[] = [
        { value: 'technology', label: 'Technology' },
        { value: 'creative', label: 'Creative' },
        { value: 'marketing', label: 'Marketing' },
        { value: 'other', label: 'Other' }
    ]

    return (
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
            <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
                {/* Header */}
                <div className="mb-8">
                    <button
                        onClick={() => navigate('/success-stories')}
                        className="flex items-center text-sm sm:text-base text-primary-600 hover:text-primary-700 mb-4 font-medium"
                    >
                        <FiArrowLeft className="w-4 h-4 mr-2 flex-shrink-0" />
                        Back to Stories
                    </button>
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl text-gray-900 dark:text-white mb-2 font-bold">Share Your Success Story</h1>
                    <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
                        Inspire others by sharing your journey. Your story will be reviewed by our team before publication.
                    </p>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl p-4 sm:p-6 md:p-8 shadow-md">
                    {/* Title */}
                    <div className="mb-6">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Story Title *
                        </label>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                            A compelling headline for your success story (10-100 characters)
                        </p>
                        <input
                            type="text"
                            name="title"
                            value={formData.title}
                            onChange={handleInputChange}
                            maxLength={100}
                            placeholder="e.g., Built a 6-figure freelance business"
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {formData.title.length}/100
                        </div>
                    </div>

                    {/* Category */}
                    <div className="mb-6">
                        <SelectDropdown
                            value={formData.category}
                            onChange={handleCategoryChange}
                            options={categoryOptions}
                            label="Category *"
                            className="w-full"
                        />
                    </div>

                    {/* Professional Info Row */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 sm:gap-6 mb-6">
                        {/* Name */}
                        <div>
                            <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                                Full Name *
                            </label>
                            <input
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleInputChange}
                                maxLength={100}
                                placeholder="Your name"
                                disabled
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
                            />
                            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                                {formData.name.length}/100
                            </div>
                        </div>

                        {/* Role */}
                        <div>
                            <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                                Current Role *
                            </label>
                            <input
                                type="text"
                                name="role"
                                value={formData.role}
                                onChange={handleInputChange}
                                maxLength={100}
                                placeholder="e.g., Freelance Developer"
                                disabled
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
                            />
                            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                                {formData.role.length}/100
                            </div>
                        </div>

                        {/* Location */}
                        <div>
                            <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                                Location *
                            </label>
                            <input
                                type="text"
                                name="location"
                                value={formData.location}
                                onChange={handleInputChange}
                                maxLength={100}
                                placeholder="e.g., New York, USA"
                                disabled
                                className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-60 disabled:cursor-not-allowed"
                            />
                            <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                                {formData.location.length}/100
                            </div>
                        </div>
                    </div>

                    {/* Achievement */}
                    <div className="mb-6">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Key Achievement *
                        </label>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                            Highlight your biggest achievement (10-100 characters)
                        </p>
                        <input
                            type="text"
                            name="achievement"
                            value={formData.achievement}
                            onChange={handleInputChange}
                            maxLength={100}
                            placeholder="e.g., Launched 15+ successful mobile apps"
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        />
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {achievementLength}/100
                        </div>
                    </div>

                    {/* Your Story */}
                    <div className="mb-6">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Your Story *
                        </label>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                            Share your journey and how SideGO helped you (50-1000 characters)
                        </p>
                        <textarea
                            name="story"
                            value={formData.story}
                            onChange={handleInputChange}
                            maxLength={1000}
                            rows={6}
                            placeholder="Tell us your story... What was your starting point? What challenges did you face? How did SideGO help? What's your current situation?"
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-vertical"
                        />
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {storyLength}/1000 characters
                        </div>
                    </div>

                    {/* Testimonial */}
                    <div className="mb-6">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Testimonial / Quote *
                        </label>
                        <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-2">
                            A powerful quote that summarizes your experience (30-500 characters)
                        </p>
                        <textarea
                            name="testimonial"
                            value={formData.testimonial}
                            onChange={handleInputChange}
                            maxLength={500}
                            rows={4}
                            placeholder='e.g., "SideGO gave me the platform to showcase my skills and connect with amazing clients."'
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 resize-vertical"
                        />
                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {testimonialLength}/500 characters
                        </div>
                    </div>

                    {/* Skills */}
                    <div className="mb-8">
                        <label className="block text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-2">
                            Key Skills * (minimum 1, maximum 10)
                        </label>
                        <div className="flex flex-col sm:flex-row gap-2 mb-4">
                            <input
                                type="text"
                                value={skillInput}
                                onChange={(e) => {
                                    setSkillInput(e.target.value)
                                    setSkillError('')
                                }}
                                onKeyPress={(e) => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault()
                                        handleAddSkill()
                                    }
                                }}
                                placeholder="e.g., React, Node.js, Python"
                                className="flex-1 px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-sm sm:text-base text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                            <button
                                type="button"
                                onClick={handleAddSkill}
                                className="px-4 sm:px-6 py-2 sm:py-3 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-lg transition-colors text-sm sm:text-base whitespace-nowrap"
                            >
                                Add
                            </button>
                        </div>

                        {skillError && (
                            <p className="text-xs sm:text-sm text-red-600 dark:text-red-400 mb-3">{skillError}</p>
                        )}

                        <div className="flex flex-wrap gap-2">
                            {formData.skills.map((skill, index) => (
                                <div
                                    key={index}
                                    className="flex items-center gap-2 px-2 sm:px-3 py-1.5 sm:py-2 bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-200 rounded-full text-xs sm:text-sm"
                                >
                                    <span>{skill}</span>
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveSkill(index)}
                                        className="hover:text-primary-900 dark:hover:text-primary-100"
                                    >
                                        <FiX className="w-3 h-3 sm:w-4 sm:h-4" />
                                    </button>
                                </div>
                            ))}
                        </div>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-2">
                            {formData.skills.length}/10 skills added
                        </p>
                    </div>

                    {/* Preview */}
                    {formData.skills.length > 0 && (
                        <div className="mb-8 p-4 sm:p-6 bg-gray-50 dark:bg-gray-700 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600">
                            <h3 className="text-sm sm:text-base font-medium text-gray-900 dark:text-white mb-4">Preview</h3>
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-3 sm:p-4">
                                <h4 className="font-heading font-bold text-sm sm:text-base text-gray-900 dark:text-white mb-2">{formData.title || 'Your Story Title'}</h4>
                                <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
                                    <span className="font-medium">Achievement:</span> {formData.achievement || 'Your achievement'}
                                </p>
                                <div className="flex flex-wrap gap-2">
                                    {formData.skills.map((skill, index) => (
                                        <span key={index} className="inline-block bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs px-2 py-1 rounded-full">
                                            {skill}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                        <button
                            type="submit"
                            disabled={loading}
                            className="flex-1 flex items-center justify-center gap-2 px-4 sm:px-6 py-2.5 sm:py-3 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-medium text-sm sm:text-base rounded-lg transition-colors"
                        >
                            {loading ? (
                                <>
                                    <FiLoader className="w-4 h-4 animate-spin" />
                                    Submitting...
                                </>
                            ) : (
                                'Submit Story'
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate('/success-stories')}
                            className="flex-1 px-4 sm:px-6 py-2.5 sm:py-3 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 text-sm sm:text-base hover:bg-gray-100 dark:hover:bg-gray-700 font-medium rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                    </div>

                    <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mt-4 text-center">
                        After submission, your story will be reviewed by our team before being published.
                    </p>
                </form>
            </div>
        </div>
    )
}

export default SubmitSuccessStoryPage